import { createFileRoute } from "@tanstack/react-router";
import { Building2, CalendarClock, Mail, ShieldCheck } from "lucide-react";

import { ContactForm } from "@/features/contact/modules/contact-form/contact-form";
import { LandingFooter } from "@/features/landing/modules/landing-footer/landing-footer";
import { LandingNav } from "@/features/landing/modules/landing-nav/landing-nav";
import { m } from "@/paraglide/messages.js";

const CONTACT_EMAIL = "mark@inginuiti.com";

const HIGHLIGHTS = [
  {
    icon: Building2,
    title: m.contact_point_pilot_title(),
    body: m.contact_point_pilot_body(),
  },
  {
    icon: ShieldCheck,
    title: m.contact_point_security_title(),
    body: m.contact_point_security_body(),
  },
  {
    icon: CalendarClock,
    title: m.contact_point_response_title(),
    body: m.contact_point_response_body(),
  },
] as const;

export const Route = createFileRoute("/(public)/contact/")({
  component: ContactPage,
  loader: () => ({ crumb: m.contact_crumb() }),
});

function ContactPage() {
  return (
    <div className="relative flex min-h-svh max-w-[100vw] flex-col overflow-x-clip bg-page-background text-foreground">
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_50%_-10%,color-mix(in_oklch,var(--primary)_12%,transparent),transparent_55%)]"
      />
      <div className="relative flex flex-1 flex-col">
        <LandingNav />
        <main className="flex-1">
          <div className="mx-auto grid w-full max-w-[1120px] gap-12 px-6 py-16 md:py-24 lg:grid-cols-[minmax(0,1fr)_minmax(0,480px)] lg:gap-16">
            <div className="flex flex-col gap-8">
              <div className="flex flex-col gap-4">
                <span className="inline-flex w-fit items-center gap-2 rounded-full border border-primary/20 bg-primary/10 px-3 py-1 text-sm font-medium text-primary">
                  <Mail className="size-4" />
                  {m.contact_badge()}
                </span>
                <h1 className="text-balance text-4xl font-semibold leading-[1.1] tracking-tight md:text-5xl">
                  {m.contact_title()}
                </h1>
                <p className="max-w-xl text-base leading-relaxed text-muted-foreground md:text-lg">
                  {m.contact_subtitle()}
                </p>
              </div>

              <ul className="flex flex-col gap-6">
                {HIGHLIGHTS.map((highlight) => (
                  <li key={highlight.title} className="flex gap-4">
                    <span className="flex size-10 shrink-0 items-center justify-center rounded-xl border border-border/60 bg-card text-primary">
                      <highlight.icon className="size-5" />
                    </span>
                    <div className="flex flex-col gap-1">
                      <span className="text-sm font-semibold text-foreground">{highlight.title}</span>
                      <span className="text-sm leading-relaxed text-muted-foreground">{highlight.body}</span>
                    </div>
                  </li>
                ))}
              </ul>

              <div className="flex flex-col gap-1 border-t border-border/60 pt-6">
                <span className="text-sm font-semibold text-foreground">{m.contact_office_heading()}</span>
                <span className="text-sm text-muted-foreground">{m.landing_footer_registration()}</span>
                <a
                  href={`mailto:${CONTACT_EMAIL}`}
                  className="mt-1 w-fit text-sm font-medium text-primary underline-offset-4 hover:underline"
                >
                  {CONTACT_EMAIL}
                </a>
              </div>
            </div>

            <ContactForm />
          </div>
        </main>
        <LandingFooter />
      </div>
    </div>
  );
}
